import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const Hero = () => {
    const { getFullTranslation } = useLanguage();
    const hero = getFullTranslation('hero');

    return (
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 flex flex-col md:flex-row items-center">
            {/* Left Content */}
            <div className="relative z-20 w-full md:w-1/2 pr-0 md:pr-12">
                <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
                    <span className="text-orange-500">{hero.title}</span><br />
                    {hero.subtitle}
                </h1>
                <p className="text-xl text-gray-500 mb-8 leading-relaxed">
                    {hero.description}
                </p>

                <p className="text-orange-600 font-medium text-sm mb-8 leading-relaxed bg-orange-50 px-4 py-3 rounded-lg border border-orange-100 inline-block">
                    {hero.newFeature}
                </p>

                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
                    <a
                        href="#download"
                        className="flex items-center justify-center bg-orange-500 text-white px-8 py-4 rounded-xl font-medium text-lg hover:bg-orange-600 transition shadow-lg shadow-orange-500/30"
                    >
                        <svg className="w-6 h-6 mr-2" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>
                        {hero.appStoreDownload} <span className="text-sm font-normal ml-2 opacity-90">(iOS)</span>
                    </a>

                    <a
                        href="#features"
                        className="flex items-center justify-center bg-white text-gray-800 border-2 border-gray-200 px-8 py-4 rounded-xl font-medium text-lg hover:bg-gray-50 transition shadow-sm"
                    >
                        {hero.learnMore}
                    </a>
                </div>

                <div className="hidden md:block">
                    <p className="text-sm text-gray-400 mb-3">{hero.iosSupport}</p>
                    <div className="flex space-x-3 text-sm font-medium text-gray-600">
                        <div className="flex items-center px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                            <span className="text-orange-500 font-bold mr-2">✔️</span> {hero.appleEcosystem}
                        </div>
                        <div className="flex items-center px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                            <span className="text-orange-500 font-bold mr-2">✔️</span> {hero.aiRecognition}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Image/Screenshot Area */}
            <div className="pointer-events-none relative z-10 w-full md:w-1/2 mt-12 md:mt-0">
                <div className="relative w-full aspect-[4/3] bg-gradient-to-tr from-orange-100 to-emerald-50 rounded-2xl shadow-2xl p-4 md:p-8 flex items-center justify-center border border-gray-200 overflow-hidden group">
                    <div className="absolute inset-0 bg-white/40 backdrop-blur-sm z-0"></div>

                    {/* Main App Mockup */}
                    <div className="relative z-10 w-64 h-[500px] bg-white rounded-[2.5rem] shadow-xl border-8 border-gray-900 overflow-hidden flex flex-col items-center">
                        {/* Dynamic Island Notional */}
                        <div className="w-1/3 h-6 bg-black rounded-b-xl absolute top-0"></div>

                        {/* Mockup UI Content */}
                        <div className="w-full h-full flex flex-col pt-10 pb-6 px-4">
                            {/* Header */}
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">今日饮食</h3>
                                    <p className="text-xs text-gray-500">还需摄入 1200 kcal</p>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold">45%</div>
                            </div>

                            {/* Cards */}
                            <div className="space-y-4 flex-1">
                                <div className="w-full h-32 bg-orange-50 rounded-2xl border border-orange-100 p-4 flex flex-col justify-between">
                                    <div className="flex justify-between">
                                        <span className="font-bold text-orange-800">早餐</span>
                                        <span className="text-sm font-medium text-orange-600">450 kcal</span>
                                    </div>
                                    <div className="flex space-x-2">
                                        <div className="w-12 h-12 bg-orange-200 rounded-xl"></div>
                                        <div className="w-12 h-12 bg-orange-200 rounded-xl"></div>
                                    </div>
                                </div>

                                <div className="w-full h-32 bg-blue-50 rounded-2xl border border-blue-100 p-4 flex flex-col justify-between">
                                    <div className="flex justify-between">
                                        <span className="font-bold text-blue-800">午餐</span>
                                        <span className="text-sm font-medium text-blue-600">由 AI 分析中...</span>
                                    </div>
                                    <div className="w-full h-2 bg-blue-200 rounded-full mt-2 overflow-hidden">
                                        <div className="w-1/2 h-full bg-blue-500 rounded-full animate-pulse"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Button */}
                            <div className="w-full mt-auto">
                                <div className="w-16 h-16 bg-orange-500 rounded-full mx-auto shadow-lg shadow-orange-400/50 flex items-center justify-center text-white text-3xl font-light">+</div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Element Mockup */}
                    <div className="absolute top-12 left-0 sm:-left-8 bg-white p-4 rounded-xl shadow-xl border border-gray-100 z-20 animate-bounce" style={{ animationDuration: '3s' }}>
                        <div className="flex items-center space-x-3">
                            <div className="text-2xl">🥗</div>
                            <div>
                                <div className="text-sm font-bold text-gray-800">轻食沙拉</div>
                                <div className="text-xs text-gray-500">约 320 千卡</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hero;
